import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { message } from 'antd';
import { useSelected } from '../../../../../providers/selectedContext';
import { useTheme } from '../../../../../providers/theme';
import { getColors } from '../../../../utils/getColors';
import { calculateSample, updateSample } from '../../../../../api/data';
import {
  CalculateSamplePayload,
  RegressionIndexes,
  UpdateSamplePayload
} from '../../../../../@types/samples/types';

import { 
  extractCurves, 
  RawDataPoint, 
  RegressionResult,
  ChartConfig
} from '../../logic';

import { generateRegressionData } from '../../logic';
import { calculateCoreyRegression } from '../../logic';
import { findOptimalSor } from '../../logic';
import { getChartOptions } from '../../logic';

export const useSonKroRegression = () => {
  const { isDay } = useTheme();
  const colors = getColors(isDay);
  const { selectedInfo, setSelectedInfo } = useSelected();
  const sample = selectedInfo?.sample;

  const [rawDataTable, setRawDataTable] = useState<RawDataPoint[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [sor, setSor] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [optimizing, setOptimizing] = useState<boolean>(false);

  const lastSampleIdForSelectionRef = useRef<string | undefined>(undefined);
  const lastSorHydrationSampleRef = useRef<string | undefined>(undefined);
  const shouldHydrateSorRef = useRef<boolean>(true);
  const resetPendingRef = useRef<boolean>(false);

  useEffect(() => {
    if (!sample) return;

    const isNewSampleContext = lastSorHydrationSampleRef.current !== sample.uid;
    if (!isNewSampleContext && !shouldHydrateSorRef.current) {
      return;
    }

    if (sample?.k_rel_values?.water_oil?.true_sor_w != null) {
      setSor(sample.k_rel_values.water_oil.true_sor_w);
    } else if (sample?.k_rel_values?.water_oil?.sor_w != null) {
      setSor(sample.k_rel_values.water_oil.sor_w);
    } else {
      setSor(0);
    }

    shouldHydrateSorRef.current = false;
    lastSorHydrationSampleRef.current = sample.uid;
  }, [sample]);

  useEffect(() => {
    setError(null);
    if (!sample) return;

    try {
      const { swArr, kArr, swi } = extractCurves(sample, 'water_oil_kro');

      if (swi == null || sor == null) {
           setRawDataTable([]);
           return;
      }
      
      const data = generateRegressionData(swArr, kArr, swi, sor, 'decreasing');
      setRawDataTable(data);

    } catch (e: any) {
      setError(e.message);
      setRawDataTable([]);
    }
  }, [sample, sor]);

  useEffect(() => {
    if (!sample || rawDataTable.length === 0) return;

    const currentDataKeys = new Set(rawDataTable.map(d => d.key));
    const isNewSample = lastSampleIdForSelectionRef.current !== sample.uid;
    
    lastSampleIdForSelectionRef.current = sample.uid;

    setSelectedRowKeys((prevSelected) => {
      let candidates: any[] = [];

      if (isNewSample) {
        candidates = sample.regression_indexes?.son_kro_selected_indexes || [];
        if (candidates.length === 0) return rawDataTable.map(d => d.key);
      } else {
        candidates = prevSelected;
        if (candidates.length === 0) candidates = sample.regression_indexes?.son_kro_selected_indexes || [];
      }

      const validKeys = candidates.filter((k: any) => currentDataKeys.has(Number(k)));

      if (validKeys.length > 0) return validKeys;
      
      return rawDataTable.map(d => d.key);
    });
    
  }, [sample, rawDataTable]);

  const regressionResult = useMemo<RegressionResult | null>(() => {
    if (rawDataTable.length < 2 || selectedRowKeys.length < 2) {
      return null;
    }
    try {
      const indices = selectedRowKeys.map(key => Number(key));
      return calculateCoreyRegression(rawDataTable, indices);
    } catch (e) {
      return null; 
    }
  }, [rawDataTable, selectedRowKeys]);

  const handleSorChange = useCallback((value: number) => {
    resetPendingRef.current = false;
    setSor(value);
  }, []);

  const handleSelectedRowKeysChange = useCallback((keys: React.Key[]) => {
    resetPendingRef.current = false;
    setSelectedRowKeys(keys);
  }, []);

  const handleReset = useCallback(() => {
    const baselineSor = sample?.k_rel_values?.water_oil?.sor_w ?? 0;
    setSor(baselineSor);
    setSelectedRowKeys(rawDataTable.map(point => point.key));
    resetPendingRef.current = true;
  }, [sample, rawDataTable]);

  const handleFindBestSor = useCallback(() => {
    if (!sample || selectedRowKeys.length < 2) {
      message.warning('Please select at least 2 points to find optimal Sor.');
      return;
    }
    setOptimizing(true);
    
    setTimeout(() => {
        try {
            const { swArr, kArr, swi } = extractCurves(sample, 'water_oil_kro');
            const indices = selectedRowKeys.map(k => Number(k));

            const result = findOptimalSor(swArr, kArr, swi, indices);

            if (result) {
                setSor(Number(result.bestSor.toFixed(4)));
                message.success(`Optimal Sor found: ${result.bestSor.toFixed(4)} (R²: ${result.bestR2.toFixed(4)})`);
            } else {
                message.warning('Could not find a valid Sor for these points.');
            }
        } catch (e: any) {
            message.error('Error optimizing Sor: ' + e.message);
        } finally {
            setOptimizing(false);
        }
    }, 100);
  }, [sample, selectedRowKeys]);

  const handleSave = useCallback(async () => {
    if (!sample) return;
    if (!regressionResult) {
        message.error("Cannot save: Invalid regression result.");
        return;
    }

    setLoading(true);
    setError(null);

    try {
      const indices = selectedRowKeys.map(key => Number(key));
      const existingIndexes = sample.regression_indexes || {};
      const indexesToSave: RegressionIndexes = {
        ...existingIndexes,
        son_kro_selected_indexes: indices, 
      };

      const payload: UpdateSamplePayload = {
        sample_uid: sample.uid,
        regression_indexes: resetPendingRef.current
          ? {
              ...existingIndexes,
              son_kro_selected_indexes: [],
              swn_krw_selected_indexes: [],
              refined_no_w_selected_indexes: [],
              refined_nw_selected_indexes: [],
            }
          : indexesToSave,
        true_sor_w: resetPendingRef.current ? null : sor,
        true_kro_w: resetPendingRef.current ? null : regressionResult.a_max_calc,
      };

      const updatedSample = await updateSample(payload);
      const refreshPayload: CalculateSamplePayload = {
        sample_uid: updatedSample.uid,
        strategies_keys: ['water-oil'],
        input_data_map: {
          'water-oil': { type: 'WATER_OIL' }
        }
      };
      const recalculatedSample = await calculateSample(refreshPayload);
      shouldHydrateSorRef.current = true;
      resetPendingRef.current = false;
      setSelectedInfo({ sample: recalculatedSample });
      message.success('Regression selection saved successfully!');
    } catch (e: any) {
      const saveError = e.message || 'Failed to save selection.';
      setError(saveError);
      message.warning(saveError);
    } finally {
      setLoading(false);
    }
  }, [sample, selectedRowKeys, regressionResult, sor, setSelectedInfo]);

  const chartOptions = useMemo(() => {
    
    const chartConfig: ChartConfig = {
        title: 'Son vs Kro Regression',
        xAxisLabel: 'Son (Normalized)',
        yAxisLabel: 'Kro (Calculated)',
        tooltipLabelX: 'Son',
        tooltipLabelY: 'Kro',
        seriesNameRaw: 'All Lab Points (Normalized)',
        seriesNameSelected: 'Points in Regression',
        seriesNameFit: 'Kro Calc (Fit Line)',
        seriesColor: colors.waterOil,
        fitLineColor: 'red',
        xAxisReversed: true
    };

    return getChartOptions({
        regressionResult,
        rawDataTable,
        selectedRowKeys,
        config: chartConfig
    });
  }, [regressionResult, rawDataTable, selectedRowKeys, colors]);

  return {
    sample,
    sor,
    setSor: handleSorChange,
    loading,
    optimizing,
    error,
    rawDataTable,
    selectedRowKeys,
    setSelectedRowKeys: handleSelectedRowKeysChange,
    regressionResult,
    chartOptions,
    handleFindBestSor,
    handleReset,
    handleSave,
    colors,
    isDay
  };
};
